/**
 * Execute Section Tool
 * 
 * Wraps ExecutionAgent as an agent-as-tool for MultiSlideBuilderAgent.
 * Creates section-level checkpoints before and after execution.
 * 
 * This tool:
 * 1. Creates "before" checkpoint
 * 2. Optionally generates a visual guide using Gemini
 * 3. Instantiates and runs ExecutionAgent for the section
 * 4. Creates "after" checkpoint
 * 5. Returns section build results
 * 
 * Updated to support new multi-slide schema with:
 * - Pre-specified areas per slide
 * - Global and per-slide context
 * - Slides copied from source with content cleared
 * - Optional visual guide generation
 */

import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import { backendClient } from "../../../../../services/communication/backendClient";
import { ExecutionAgent } from "../../executionAgent";
import type { ExecutionAgentResponse } from "../../executionAgent.schema";
import {
    ExecuteSectionParamsSchema,
    type ExecuteSectionParams,
    type ExecuteSectionResponse,
} from "./executeSection.schema";
import { SlideGuideGenerator } from "../../services/slideGuideGenerator";

// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const EXECUTE_SECTION_SYSTEM_PROMPT = `### executeSection
Execute a section build using the Execution Agent.

**When to use:**
- Building a section from the plan during Big Slide Gen
- Called once per section in the plan

**What it does:**
1. Creates checkpoint BEFORE section build
2. Runs ExecutionAgent to build all slides in the section
3. Creates checkpoint AFTER section build
4. Returns results with checkpoint IDs for rollback

**If section fails:** You can rollback to checkpoint_before and retry.

\`\`\`json
{
  "planId": "plan_abc123",
  "sectionId": "exec_summary",
  "sectionTitle": "Executive Summary",
  "slides": [
    {
      "slideIndex": 1,
      "purpose": "High-level summary",
      "areas": [{ "areaId": "header", "region": {...}, "contentType": "text" }],
      "context": [{ "context": ["as_abc"], "purpose": "Key metrics" }]
    }
  ],
  "context": [{ "context": ["slide_5"], "purpose": "Style reference" }]
}
\`\`\``;

// ═══════════════════════════════════════════════════════════════════════════
// EXECUTE SECTION TOOL
// ═══════════════════════════════════════════════════════════════════════════

export class ExecuteSectionTool extends ToolBase<ExecuteSectionParams, ExecuteSectionResponse> {
    public readonly name = "executeSection";
    public readonly description: ToolDescription;
    public readonly isAgent = true; // This wraps an agent
    
    // System prompt description
    public readonly systemPromptDescription = EXECUTE_SECTION_SYSTEM_PROMPT;
    
    /** Plan ID for this build session */
    private planId: string;
    
    /** Slide dimensions in points (injected from parent agent) */
    private slideDimensions: { width: number; height: number };
    
    /** Number of style guide slides (for index offset in readSlide) */
    private styleGuideSlideCount: number;
    
    constructor(planId: string, slideDimensions: { width: number; height: number }, styleGuideSlideCount: number = 0) {
        super();
        this.planId = planId;
        this.slideDimensions = slideDimensions;
        this.styleGuideSlideCount = styleGuideSlideCount;
        
        this.description = new ToolDescription(
            "executeSection",
            `Execute a section build using the Execution Agent.

This tool:
1. Creates a checkpoint BEFORE the section build
2. Runs the Execution Agent to build all slides in the section
3. Creates a checkpoint AFTER the section build
4. Returns the results with checkpoint IDs for rollback

The Execution Agent will:
- Insert slides by copying from source and clearing content
- Build each specified area using buildArea tool
- Evaluate screenshots to verify quality
- Auto-checkpoint after every area (5-digit IDs)

Use this tool for each section in your plan.
If section fails, you can rollback to checkpoint_before.

Example:
{
  "planId": "plan_123",
  "sectionId": "exec_summary",
  "sectionTitle": "Executive Summary",
  "globalContext": [
    { "context": ["logo_001"], "purpose": "Company logo for all slides" }
  ],
  "referenceSlideIndices": [0, 2],
  "slides": [
    {
      "sourceSlideIndex": 1,
      "position": 0,
      "title": "Overview",
      "purpose": "High-level summary of findings",
      "areas": [
        { "areaId": "header", "region": { "x": 0.05, "y": 0.05, "width": 0.9, "height": 0.1 }, "purpose": "Slide title", "contentType": "text" },
        { "areaId": "body", "region": { "x": 0.05, "y": 0.2, "width": 0.9, "height": 0.7 }, "purpose": "Main content", "contentType": "mixed" }
      ],
      "context": [
        { "context": ["as_001", "as_002"], "purpose": "Research findings" }
      ]
    }
  ]
}`,
            ExecuteSectionParamsSchema
        );
    }
    
    /**
     * Execute the section build
     */
    public async execute(parameters: unknown): Promise<ExecuteSectionResponse> {
        const parseResult = safeParseToolParams(ExecuteSectionParamsSchema, parameters);
        
        if (!parseResult.success) {
            console.error('[ExecuteSectionTool] Invalid parameters:', parseResult.error);
            return {
                success: false,
                sectionId: 'unknown',
                sectionTitle: 'unknown',
                slidesBuilt: 0,
                areasBuilt: 0,
                checkpointBefore: '',
                checkpointAfter: '',
                errors: [`Invalid parameters: ${parseResult.error}`],
            };
        }
        
        const params = parseResult.data;
        
        // Generate a section index if not provided (use hash of sectionId or 0)
        const sectionIndex = params.sectionId 
            ? params.sectionId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 1000
            : 0;
        
        console.log(`[ExecuteSectionTool] Executing section: ${params.sectionTitle ?? 'Untitled'} (${params.slides.length} slides)`);
        
        let checkpointBefore = '';
        let checkpointAfter = '';
        let visualGuides: Array<{ slideIndex: number; imageBase64: string; instructions: string }> = [];
        
        try {
            // 1. Create BEFORE checkpoint
            console.log(`[ExecuteSectionTool] Creating before checkpoint for section ${sectionIndex}`);
            const beforeResponse = await backendClient.createBuildCheckpoint(
                this.planId,
                {
                    checkpointType: 'section_before',
                    sectionIndex,
                }
            );
            checkpointBefore = beforeResponse.checkpoint_id;
            console.log(`[ExecuteSectionTool] Before checkpoint: ${checkpointBefore}`);
            
            // 2. Optionally generate visual guides for ALL slides using Gemini
            if (params.generateVisualGuide) {
                console.log(`[ExecuteSectionTool] Generating visual guides for ${params.slides.length} slides with Gemini...`);
                visualGuides = await this.generateVisualGuides(params);
                console.log(`[ExecuteSectionTool] Generated ${visualGuides.length}/${params.slides.length} visual guides`);
            }
            
            // 3. Create and run Execution Agent
            const executionAgent = new ExecutionAgent(this.planId);
            
            console.log(`[ExecuteSectionTool] Starting ExecutionAgent for ${params.slides.length} slides with ${params.slides.reduce((sum, s) => sum + s.areas.length, 0)} total areas`);
            console.log(`[ExecuteSectionTool] Using slide dimensions: ${this.slideDimensions.width} x ${this.slideDimensions.height} points`);
            
            // Inject slideDimensions, styleGuideSlideCount, and visual guides
            const agentParams = {
                ...params,
                slideDimensions: this.slideDimensions,
                styleGuideSlideCount: this.styleGuideSlideCount,
                visualGuides: visualGuides.length > 0 ? visualGuides : undefined,
            };
            
            const agentResult = await executionAgent.execute(agentParams, {
                // No streaming callbacks needed
            });
            
            // Check if agent succeeded (AgentResult has status, not success)
            const isSuccess = agentResult.status === 'success';
            console.log(`[ExecuteSectionTool] ExecutionAgent completed: ${isSuccess ? 'success' : 'failed'}`);
            
            // Extract result data from the first result item
            const executionResult = agentResult.results[0]?.relevantResults as ExecutionAgentResponse | undefined;
            
            // Calculate total areas built
            const areasBuilt = executionResult?.areaResults?.length ?? 0;
            
            // 4. Create AFTER checkpoint
            console.log(`[ExecuteSectionTool] Creating after checkpoint for section ${sectionIndex}`);
            const afterResponse = await backendClient.createBuildCheckpoint(
                this.planId,
                {
                    checkpointType: 'section_after',
                    sectionIndex,
                }
            );
            checkpointAfter = afterResponse.checkpoint_id;
            console.log(`[ExecuteSectionTool] After checkpoint: ${checkpointAfter}`);
            
            // 5. Return results
            return {
                success: isSuccess,
                sectionId: params.sectionId,
                sectionTitle: params.sectionTitle,
                slidesBuilt: executionResult?.slidesBuilt ?? 0,
                areasBuilt,
                checkpointBefore,
                checkpointAfter,
                errors: executionResult?.errors,
                executionResult,
            };
            
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error(`[ExecuteSectionTool] Error: ${errorMessage}`);
            
            // On failure, automatically rollback to checkpoint_before
            if (checkpointBefore) {
                console.log(`[ExecuteSectionTool] Rolling back to checkpoint: ${checkpointBefore}`);
                try {
                    await backendClient.rollbackBuild(this.planId, checkpointBefore);
                    console.log(`[ExecuteSectionTool] Rollback successful`);
                } catch (rollbackError) {
                    console.error(`[ExecuteSectionTool] Rollback failed:`, rollbackError);
                }
            }
            
            return {
                success: false,
                sectionId: params.sectionId,
                sectionTitle: params.sectionTitle,
                slidesBuilt: 0,
                areasBuilt: 0,
                checkpointBefore,
                checkpointAfter,
                errors: [errorMessage],
                rolledBack: true, // Indicate rollback happened
            };
        }
    }
    
    /**
     * Generate visual guides/blueprints for ALL slides using Gemini
     * 
     * This calls the SlideGuideGenerator service to create visual targets
     * for each slide. The guides are generated before the agent runs
     * and passed as context to help the LLM understand the expected output.
     * 
     * @param params - The section execution params
     * @returns Array of visual guides with slide index, image, and instructions
     */
    private async generateVisualGuides(
        params: ExecuteSectionParams
    ): Promise<Array<{ slideIndex: number; imageBase64: string; instructions: string }>> {
        const guides: Array<{ slideIndex: number; imageBase64: string; instructions: string }> = [];
        
        if (!params.slides || params.slides.length === 0) {
            console.log(`[ExecuteSectionTool] No slides to generate guides for`);
            return guides;
        }
        
        const generator = new SlideGuideGenerator(this.planId);
        
        // Build master data (shared across all slides)
        const masterData = {
            contentArea: params.styleGuideInfo?.contentArea,
            exclusionZones: params.styleGuideInfo?.exclusionZones,
            slideDimensions: this.slideDimensions,
        };
        
        // Convert style guide info to style patterns (shared)
        const stylePatterns = params.styleGuideInfo?.topStyleSignatures
            ? SlideGuideGenerator.convertStyleGuideToPatterns({
                topStyleSignatures: params.styleGuideInfo.topStyleSignatures as Array<{
                    style?: {
                        font_family?: string;
                        font_size?: number;
                        font_color?: string;
                        fill_color?: string;
                    };
                    usage_percentage?: number;
                }>,
            })
            : undefined;
        
        // Reference images (shared)
        const referenceImages = params.referenceImages || [];
        
        // Generate a guide for each slide
        for (let i = 0; i < params.slides.length; i++) {
            const slide = params.slides[i];
            
            try {
                // Build slide-specific instructions
                const instructions = SlideGuideGenerator.buildInstructionsFromContext(
                    slide.title,
                    slide.purpose,
                    slide.areas[0]?.contentType || 'mixed',
                    // Include section context and slide position
                    [
                        params.sectionTitle ? `Section: ${params.sectionTitle}` : '',
                        `Slide ${i + 1} of ${params.slides.length} in this section`,
                        `Areas to build: ${slide.areas.map(a => `${a.areaId} (${a.contentType})`).join(', ')}`,
                    ].filter(Boolean)
                );
                
                console.log(`[ExecuteSectionTool] Generating guide for slide ${i + 1}: "${slide.title}"`);
                
                const result = await generator.generateGuide({
                    instructions,
                    masterData,
                    referenceImages,
                    stylePatterns,
                });
                
                if (result.success && result.imageBase64) {
                    guides.push({
                        slideIndex: i,
                        imageBase64: result.imageBase64,
                        instructions,
                    });
                    console.log(`[ExecuteSectionTool] ✓ Guide generated for slide ${i + 1}`);
                } else {
                    console.log(`[ExecuteSectionTool] ✗ Guide failed for slide ${i + 1}: ${result.error || 'No image returned'}`);
                }
                
            } catch (error) {
                console.error(`[ExecuteSectionTool] Error generating guide for slide ${i + 1}:`, error);
                // Continue with other slides even if one fails
            }
        }
        
        return guides;
    }
}

export default ExecuteSectionTool;
